import { useMemo, useState } from 'react';
import { Plus, Pencil, History, Check, Send, RotateCcw, Archive, Sparkles, Globe, Copy } from 'lucide-react';
import { api } from '../../lib/api';
import { easternDay } from '../../../modules/q2c/eastern-day.mjs';
import Modal from '../ui/Modal';
import { Chip, fmtDate, inputCls } from '../bids/FieldGrid';
import {
  card, iconBtn, primaryBtn, opts, useList, FormModal, HistoryModal, Empty, CHANNEL_LABEL, CONTENT_STATUS, CONTENT_TONE, fmtDateTime,
} from './shared';

function contentFields(segments) {
  return [
    { key: 'channel', label: 'Channel', type: 'select', options: opts(CHANNEL_LABEL), required: true },
    { key: 'title', label: 'Title / subject', required: true },
    { key: 'body', label: 'Text', type: 'textarea', rows: 10, hint: 'The exact words to post or send. Changing approved text sends it back to draft.' },
    { key: 'segment_id', label: 'Target list', type: 'select', options: segments.map((s) => ({ value: s.id, label: s.name })), empty: 'None' },
    { key: 'audience_note', label: 'Audience note', hint: 'e.g. about 40,000 LinkedIn contacts' },
    { key: 'target_date', label: 'Target date', type: 'date' },
    { key: 'notes', label: 'Notes', type: 'textarea', rows: 3 },
  ];
}

function AiDraftModal({ item, onUse, onClose }) {
  const [brief, setBrief] = useState('');
  const [reference, setReference] = useState(item?.notes || '');
  const [channel, setChannel] = useState(item?.channel || 'linkedin_post');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  async function run() {
    setBusy(true); setErr('');
    try { const r = await api.post('/marketing/content/ai-draft', { channel, title: item?.title, brief, reference }); setText(r.text); } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  const close = () => { if (!saving) onClose(); };
  return (
    <Modal title="Draft with AI" onClose={close} maxWidth="max-w-3xl">
      <p className="mb-3 text-xs text-[var(--text-secondary)]">The AI only writes a suggestion. Nothing is saved until you click Use this text and then Save, and nothing is ever posted or sent from the portal.</p>
      <div className="grid gap-3">
        <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Channel</span>
          <select className={inputCls} value={channel} onChange={(e) => setChannel(e.target.value)}>{Object.entries(CHANNEL_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
        <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Brief *</span>
          <textarea className={inputCls} rows={3} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="What should this say, to whom, and what should they do next?" /></label>
        <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Facts it may use</span>
          <textarea className={inputCls} rows={4} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Paste the facts, quotes or numbers the draft can rely on." /></label>
        <div><button disabled={busy || !brief.trim()} onClick={run} className={primaryBtn}><Sparkles size={14} /> {busy ? 'Drafting…' : 'Draft'}</button></div>
        {err && <p className="text-sm text-rose-600">{err}</p>}
        {text && <textarea className={inputCls} rows={12} value={text} onChange={(e) => setText(e.target.value)} />}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button disabled={saving} onClick={close} className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm">Cancel</button>
        <button disabled={saving || !text.trim()} onClick={async () => {
          // Keep the draft available when saving it fails.
          setSaving(true); setErr('');
          const why = await onUse({ channel, body: text });
          if (why) { setErr(why); setSaving(false); }
        }} className={primaryBtn}>Use this text</button>
      </div>
    </Modal>
  );
}

function PostedModal({ item, onDone, onClose }) {
  const [url, setUrl] = useState(item.posted_url || '');
  const [on, setOn] = useState(() => easternDay(new Date()));
  const [err, setErr] = useState('');
  async function save() {
    try { await api.post(`/marketing/content/${item.id}/posted`, { posted_url: url || null, posted_on: on }); onDone(); } catch (e) { setErr(e.message); }
  }
  return (
    <Modal title="Mark as posted" onClose={onClose}>
      <p className="mb-3 text-sm text-[var(--text-secondary)]">Post or send it yourself first (LinkedIn, your email tool). This only records that it went out.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm sm:col-span-2"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Link to the post (optional)</span><input className={inputCls} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.linkedin.com/…" /></label>
        <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Posted on</span><input type="date" className={inputCls} value={on} onChange={(e) => setOn(e.target.value)} /></label>
      </div>
      {err && <p className="mt-2 text-sm text-rose-600">{err}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm">Cancel</button>
        <button onClick={save} className={primaryBtn}><Send size={14} /> Mark posted</button>
      </div>
    </Modal>
  );
}

export default function ContentTab({ segments }) {
  const { rows, err, load, setErr } = useList('/marketing/content', 'content');
  const [edit, setEdit] = useState(null);
  const [ai, setAi] = useState(null);
  const [posted, setPosted] = useState(null);
  const [hist, setHist] = useState(null);
  const [view, setView] = useState(null);
  const [filter, setFilter] = useState('open');
  const fields = useMemo(() => contentFields(segments), [segments]);
  const shown = (rows || []).filter((r) => (filter === 'open' ? ['draft', 'approved'].includes(r.status) : filter === 'all' ? true : r.status === filter));

  const act = async (fn) => { try { await fn(); await load(); } catch (e) { setErr(e.message); } };
  const save = async (payload) => {
    if (edit?.id) await api.patch(`/marketing/content/${edit.id}`, payload); else await api.post('/marketing/content', payload);
    setEdit(null); await load();
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          {[['open', 'Drafts and approved'], ['draft', 'Drafts'], ['approved', 'Approved'], ['posted', 'Posted'], ['archived', 'Archived'], ['all', 'All']].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`rounded-full px-3 py-1 text-xs ${filter === k ? 'bg-primary text-white' : 'border border-[var(--border)]'}`}>{l}</button>
          ))}
        </div>
        <button className={primaryBtn} onClick={() => setEdit({})}><Plus size={14} /> New draft</button>
      </div>
      <p className="text-xs text-[var(--text-secondary)]">Draft, then one partner approves the exact text, then whoever posts it marks it posted. The portal never posts to LinkedIn or sends email.</p>
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!rows && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      {rows && !shown.length && <Empty>Nothing here yet.</Empty>}
      <div className="space-y-2">
        {shown.map((c) => (
          <div key={c.id} className={card}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Chip tone={CONTENT_TONE[c.status]}>{CONTENT_STATUS[c.status]}</Chip>
                  <span className="text-xs text-[var(--text-secondary)]">{CHANNEL_LABEL[c.channel]}</span>
                  {c.ai_assisted && <Chip tone="indigo">AI assisted</Chip>}
                  {c.target_date && <span className="text-xs text-[var(--text-secondary)]">Target {fmtDate(c.target_date)}</span>}
                </div>
                <button className="mt-1 text-left font-medium text-[var(--text-primary)] hover:underline" onClick={() => setView(c)}>{c.title}</button>
                <div className="text-xs text-[var(--text-secondary)]">
                  {c.segment_name && <>List: {c.segment_name} · </>}{c.audience_note && <>{c.audience_note} · </>}
                  {c.approved_by && <>Approved by {c.approved_by.split('@')[0]} {fmtDateTime(c.approved_at)} · </>}
                  {c.posted_at && <>Posted {fmtDate(c.posted_at)} by {c.posted_by?.split('@')[0]}{c.posted_url && <> · <a className="text-primary underline" href={c.posted_url} target="_blank" rel="noreferrer noopener">link</a></>}</>}
                </div>
                {!c.body && <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">No text yet.</div>}
              </div>
              <div className="flex flex-wrap gap-1">
                {c.status !== 'archived' && <button className={iconBtn} onClick={() => setEdit(c)}><Pencil size={12} /> Edit</button>}
                {c.status === 'draft' && <button className={iconBtn} onClick={() => setAi(c)}><Sparkles size={12} /> AI draft</button>}
                {c.status === 'draft' && <button className={iconBtn} disabled={!c.body} onClick={() => act(() => api.post(`/marketing/content/${c.id}/approve`))}><Check size={12} /> Approve</button>}
                {c.status === 'approved' && <button className={iconBtn} onClick={() => setPosted(c)}><Send size={12} /> Mark posted</button>}
                {c.status === 'approved' && c.channel === 'blog_post' && !c.blog_post_id && <button className={iconBtn} onClick={() => act(() => api.post(`/marketing/content/${c.id}/to-blog`))}><Globe size={12} /> Put on website (unpublished)</button>}
                {['approved', 'archived'].includes(c.status) && !c.posted_at && <button className={iconBtn} onClick={() => act(() => api.post(`/marketing/content/${c.id}/reopen`))}><RotateCcw size={12} /> Back to draft</button>}
                {c.status !== 'archived' && <button className={iconBtn} onClick={() => act(() => api.post(`/marketing/content/${c.id}/archive`))}><Archive size={12} /> Archive</button>}
                <button className={iconBtn} onClick={() => setHist(c)}><History size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {edit && (
        <FormModal title={edit.id ? 'Edit content' : 'New draft'} fields={fields} record={edit} onSave={save} onClose={() => setEdit(null)} />
      )}
      {ai && (
        <AiDraftModal item={ai} onClose={() => setAi(null)} onUse={async ({ channel, body }) => {
          try { await api.patch(`/marketing/content/${ai.id}`, { channel, body, ai_assisted: true }); } catch (e) { return e.message; }
          setAi(null);
          await load();
        }} />
      )}
      {posted && <PostedModal item={posted} onClose={() => setPosted(null)} onDone={async () => { setPosted(null); await load(); }} />}
      {hist && <HistoryModal table="mkt_content" id={hist.id} title={hist.title} onClose={() => setHist(null)} />}
      {view && (
        <Modal title={view.title} onClose={() => setView(null)} maxWidth="max-w-3xl">
          <div className="mb-2 flex items-center gap-2"><Chip tone={CONTENT_TONE[view.status]}>{CONTENT_STATUS[view.status]}</Chip><span className="text-xs text-[var(--text-secondary)]">{CHANNEL_LABEL[view.channel]}</span>
            {view.body && <button className={iconBtn} onClick={() => navigator.clipboard?.writeText(view.body)}><Copy size={12} /> Copy text</button>}</div>
          <pre className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 font-sans text-sm dark:bg-slate-800">{view.body || 'No text yet.'}</pre>
          {view.notes && <p className="mt-3 whitespace-pre-wrap text-xs text-[var(--text-secondary)]">{view.notes}</p>}
          {view.sources?.length > 0 && <p className="mt-2 text-xs text-[var(--text-secondary)]">Sources: {view.sources.join('; ')}</p>}
        </Modal>
      )}
    </div>
  );
}
